<template>
  <div class="h-full flex bg-grey-navigation text-surface-user-blue">
    <div class="twilio-pip-dialer flex h-full flex-col w-full">
      <!-- Phone Number Header -->
      <div
        v-if="activeUserPhoneNumber && hasActiveCall"
        class="flex items-center justify-between border-b border-white/10 p-4"
      >
        <div>
          {{
            isOutgoing
              ? $t("DIALER.OUTGOING_CALL_FROM")
              : $t("DIALER.INCOMING_CALL_TO")
          }}:
        </div>
        <div class="flex items-center space-x-2">
          <div
            v-tooltip="$t('COMMON.COPY_TO_CLIPBOARD')"
            class="cursor-pointer"
            @click.stop.prevent="
              copyToClipboard(activeUserPhoneNumber, { isPiP: true })
            "
          >
            {{ activeUserPhoneNumber }}
          </div>
          <twilio-reset-button @reset="resetState" />
        </div>
      </div>

      <!-- Status Header (shown when no active call) -->
      <div
        v-else
        class="flex items-center justify-between border-b border-white/10 p-4"
      >
        <span class="font-semibold">{{ $t("DIALER.TITLE") }}</span>
        <div class="flex items-center space-x-2">
          <div
            v-if="deviceStatus"
            class="flex items-center space-x-2 text-xs"
            :class="statusClass"
          >
            <span class="size-2 rounded-full" :class="statusDotClass" />
            <span>{{ deviceStatus }}</span>
          </div>
          <twilio-reset-button @reset="resetState" />
        </div>
      </div>

      <!-- Call Info -->
      <div v-if="hasActiveCall" class="flex-1">
        <div
          class="flex w-full items-center justify-between border-b border-white/10 p-4"
        >
          <div class="flex items-center space-x-3 text-surface-user-blue">
            <lf-avatar
              :username="contactName || $t('COMMON.UNKNOWN')"
              :size="40"
            />
            <div class="flex min-w-0 flex-col">
              <span v-tooltipable.themeLight class="truncate font-semibold">
                {{ contactName || $t("COMMON.UNKNOWN") }}
              </span>
              <span data-cy="to-number" class="text-xs text-label">
                {{ contactNumber || "-" }}
              </span>
            </div>
          </div>
          <call-timer v-if="showCallTimer" class="shrink-0 min-w-12" />
        </div>
        <div class="call-details flex w-full flex-col space-y-1 p-4">
          <div
            v-for="row in infoRows"
            :key="row.name"
            :data-cy="row.name"
            class="call-session-info-row group"
          >
            <icon-base
              width="12"
              height="12"
              :view-box="row.iconOptions.viewBox"
              :icon="row.iconOptions.icon"
            />
            <span v-tooltipable.themeLight class="truncate">
              {{ row.value }}
            </span>
            <a
              v-if="row.link"
              :href="getRouteHref(row.link)"
              target="_opener"
              class="cursor-pointer"
            >
              <icon-base
                width="12"
                height="12"
                view-box="0 0 24 24"
                :icon="IconLink"
              />
            </a>
          </div>
        </div>
      </div>

      <!-- Idle State -->
      <div
        v-else
        class="flex flex-1 flex-col items-center justify-center p-4 text-center"
      >
        <icon-base
          :icon="IconDialerPhone"
          width="48"
          height="48"
          class="mb-4 text-label"
        />
        <p class="text-sm text-label">
          {{ $t("DIALER.WAITING_FOR_CALLS") }}
        </p>
        <p class="mt-2 text-xs text-label/60">
          {{ $t("DIALER.PIP_ACTIVE") }}
        </p>
      </div>

      <!-- Incoming Call Actions -->
      <div v-if="hasIncomingCall" class="twilio-dialer-actions">
        <div
          :title="$t('DIALER.IGNORE')"
          class="dialer-round-button flex-none bg-white/10"
          @click.stop.prevent="toggleSilence"
        >
          <icon-base
            :icon="isRingtoneSilenced ? IconSpeakerMute : IconSpeaker"
          />
        </div>
        <lf-button
          type="error"
          class="space-x-2 bg-error"
          centered
          @click.stop.prevent="rejectCall"
        >
          <template #icon>
            <icon-base :icon="IconDialerPhone" class="rotate-135" />
          </template>
          <span>{{ $t("DIALER.DECLINE") }}</span>
        </lf-button>
        <lf-button
          type="success"
          class="space-x-2 bg-success"
          centered
          @click.stop.prevent="acceptCall"
        >
          <template #icon>
            <icon-base :icon="IconDialerPhone" />
          </template>
          <span>{{ $t("DIALER.ACCEPT") }}</span>
        </lf-button>
      </div>

      <!-- Ongoing Call Actions -->
      <div v-else-if="hasOngoingCall" class="twilio-dialer-actions">
        <div
          :title="$t('DIALER.SHOW_KEYPAD')"
          class="flex size-12 cursor-pointer items-center justify-center rounded-full bg-white/10 hover:bg-label"
          @click.stop.prevent="toggleKeypad"
        >
          <icon-base :icon="IconCallKeypad" class="text-surface-user-blue" />
        </div>
        <div
          :title="isMicrophoneMuted ? $t('DIALER.UNMUTE') : $t('DIALER.MUTE')"
          class="flex size-12 cursor-pointer items-center justify-center rounded-full bg-white/10 hover:bg-label"
          @click.stop.prevent="toggleMute"
        >
          <icon-base
            :icon="isMicrophoneMuted ? IconMicrophoneMute : IconMicrophone"
          />
        </div>
        <div
          :title="$t('COMMON.DISCONNECT')"
          class="flex size-12 cursor-pointer items-center justify-center rounded-full bg-error"
          @click.stop.prevent="disconnectCall"
        >
          <icon-base :icon="IconDialerPhone" class="rotate-135" />
        </div>
      </div>

      <!-- Device Selection -->
      <template v-if="device && isOutgoing">
        <twilio-device-select
          :devices="groupedDevices.input"
          :selected-device="selectedInputDeviceId"
          type="input"
          @device:select="setDeviceSettings"
          @device:refresh="authorizeUseMedia"
        />
        <twilio-device-select
          v-if="device?.audio?.isOutputSelectionSupported"
          :devices="groupedDevices.output"
          :selected-device="selectedOutputDeviceId"
          type="output"
          @device:select="setDeviceSettings"
          @device:refresh="authorizeUseMedia"
        />
      </template>
    </div>
    <call-keypad v-if="isKeypadVisible" @keypad:press="handleKeypadPress" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRaw, watch } from "vue";
import { useI18n } from "vue-i18n";
import { type RouteLocationRaw, useRouter } from "vue-router";
import { useStore } from "vuex";

import * as Sentry from "@sentry/vue";
import { storeToRefs } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import {
  CallSessionStatus,
  CommunicationType,
  PhoneSelectionType,
} from "@/enums/communicationLogs";
import {
  TwilioCallActions,
  TwilioCallDirection,
  TwilioCallErrors,
  TwilioDeviceState,
} from "@/enums/communicationProviders";
import { AudioDevicePreferences } from "@/enums/preferences";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { useTwilio } from "@/hooks/communicationProviders";
import { useNotification } from "@/hooks/notifications";
import { useAudioDeviceMutation } from "@/hooks/queries/profile/useAudioDeviceMutation";
import { useAudioDevicePreferences } from "@/hooks/useAudioDevicePreferences";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";
import { useAnalyticsStore } from "@/stores/analytics";
import { useCallsStore } from "@/stores/calls";

import IconCallKeypad from "@/components/icons/IconCallKeypad.vue";
import IconDialerPhone from "@/components/icons/IconDialerPhone.vue";
import IconLink from "@/components/icons/IconLink.vue";
import IconMicrophone from "@/components/icons/IconMicrophone.vue";
import IconMicrophoneMute from "@/components/icons/IconMicrophoneMute.vue";
import IconPersons from "@/components/icons/IconPersons.vue";
import IconProfileSmall from "@/components/icons/IconProfileSmall.vue";
import IconSpeaker from "@/components/icons/IconSpeaker.vue";
import IconSpeakerMute from "@/components/icons/IconSpeakerMute.vue";
import IconDeals from "@/components/icons/sidebar/IconDeals.vue";
import CallKeypad from "@/components/twilio/CallKeypad.vue";
import CallTimer from "@/components/twilio/CallTimer.vue";
import TwilioDeviceSelect from "@/components/twilio/TwilioDeviceSelect.vue";
import TwilioResetButton from "@/components/twilio/TwilioResetButton.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfAvatar from "@/components/ui/LFAvatar.vue";
import LfButton from "@/components/ui/buttons/LfButton.vue";

const emit = defineEmits<{
  "keypad:toggle": [boolean];
}>();

const { t } = useI18n();
const router = useRouter();
const { trackEvent } = useAnalyticsStore();

const callStore = useCallsStore();
const {
  callDialog,
  hasIncomingCall,
  hasOngoingCall,
  isMicrophoneMuted,
  isRingtoneSilenced: storeIsRingtoneSilenced,
  device,
  isCallActive,
  isDialing,
  deviceState,
  callDirection,
  activeCallSession,
  currentCall,
  incomingCall,
  activeCall,
  isRingtoneSilenced,
  isKeypadVisible,
  showCallTimer,
} = storeToRefs(callStore);
const { savedPhoneNumber } = useTwilio();
const { showMessage } = useNotification();
const copyToClipboard = useClipboardWithMessage();
const { restoreDevice } = useAudioDevicePreferences();

const audioDevices = ref<MediaDeviceInfo[] | null>(null);
const hasRestoredDevices = ref(false);
const selectedInputDeviceId = ref<string>();
const selectedOutputDeviceId = ref<string>();
const previousDeviceIds = ref<Record<string, string | undefined>>({});

const hasActiveCall = computed(() => isCallActive.value || isDialing.value);

const groupedDevices = computed(() => {
  const empty: { input: MediaDeviceInfo[]; output: MediaDeviceInfo[] } = {
    input: [],
    output: [],
  };
  if (!audioDevices.value) {
    return empty;
  }
  return audioDevices.value.reduce((acc, curr) => {
    const type =
      curr.kind === "audioinput"
        ? "input"
        : curr.kind === "audiooutput"
          ? "output"
          : null;
    if (type) {
      acc[type].push(curr);
    }
    return acc;
  }, empty);
});

const store = useStore();

const { mutate: saveDevicePreference } = useAudioDeviceMutation({
  onSuccess: ({ type, deviceId, label }) => {
    const user = store.getters["auth/user"];
    if (!user) {
      return;
    }
    const key =
      type === "input"
        ? AudioDevicePreferences.input
        : AudioDevicePreferences.output;
    const newPref = { key, value: { device_id: deviceId, label } };
    const preferences = user.preferences ?? [];
    const index = preferences.findIndex((p: { key: string }) => p.key === key);
    const updatedPreferences =
      index >= 0
        ? preferences.map((p: { key: string }, i: number) =>
            i === index ? newPref : p
          )
        : [...preferences, newPref];
    store.commit("auth/setUpdatedAuthUser", {
      preferences: updatedPreferences,
    });
  },
  onError: ({ type }) => {
    const previousId = previousDeviceIds.value[type];

    if (!previousId || !device.value) {
      return;
    }
    if (type === "input") {
      selectedInputDeviceId.value = previousId;
      toRaw(device.value).audio?.setInputDevice(previousId);

      return;
    }
    selectedOutputDeviceId.value = previousId;
    toRaw(device.value).audio?.speakerDevices?.set(previousId);
  },
});

const deviceStatus = computed(() => {
  const stateMap = {
    [TwilioDeviceState.Idle]: t("DIALER.READY"),
    [TwilioDeviceState.Incoming]: t("DIALER.INCOMING_CALL_TO"),
    [TwilioDeviceState.Ongoing]: t("DIALER.ONGOING_CALL"),
    [TwilioDeviceState.Ringing]: t("DIALER.RINGING"),
    [TwilioDeviceState.Initiating]: t("DIALER.RINGING"),
  };
  return stateMap[deviceState.value] ?? null;
});

const statusClass = computed(() => ({
  "text-success": deviceState.value === TwilioDeviceState.Idle && device.value,
  "text-warning": deviceState.value === TwilioDeviceState.Incoming,
  "text-primary": deviceState.value === TwilioDeviceState.Ongoing,
}));

const statusDotClass = computed(() => ({
  "bg-success": deviceState.value === TwilioDeviceState.Idle && device.value,
  "bg-warning animate-pulse": deviceState.value === TwilioDeviceState.Incoming,
  "bg-primary": deviceState.value === TwilioDeviceState.Ongoing,
}));

const isOutgoing = computed(
  () => callDirection.value === TwilioCallDirection.Outgoing
);

const contactName = computed(() => {
  if (isOutgoing.value) {
    return activeCallSession.value?.data?.callee?.name ?? null;
  }
  const call = currentCall.value;
  const rawCall = call ? toRaw(call) : null;
  return rawCall?.customParameters?.get("callerFullName") ?? null;
});

const contactNumber = computed(() => {
  if (isOutgoing.value) {
    return activeCallSession.value?.to ?? null;
  }
  const call = currentCall.value;
  const rawCall = call ? toRaw(call) : null;
  return rawCall?.parameters?.From ?? rawCall?.parameters?.To ?? null;
});

const businessName = computed(() => {
  if (isOutgoing.value) {
    return (
      activeCallSession.value?.data?.call_sessionable?.business
        ?.business_legal_name ?? null
    );
  }
  const call = currentCall.value;
  const rawCall = call ? toRaw(call) : null;
  return rawCall?.customParameters?.get("callerBusinessName") ?? null;
});

const partnerName = computed(() => {
  if (isOutgoing.value) {
    return activeCallSession.value?.partner ?? null;
  }
  const call = currentCall.value;
  const rawCall = call ? toRaw(call) : null;
  return rawCall?.customParameters?.get("callerIntegrationPartner") ?? null;
});

const activeUserPhoneNumber = computed(() => {
  if (isOutgoing.value) {
    return (
      activeCallSession.value?.data?.from ??
      savedPhoneNumber.value?.phone_number ??
      PhoneSelectionType.Auto
    );
  }
  const call = currentCall.value;
  const rawCall = call ? toRaw(call) : null;
  return (
    rawCall?.customParameters?.get("to") ?? rawCall?.parameters?.To ?? null
  );
});

const dealLink = computed(() => {
  if (isOutgoing.value) {
    return undefined;
  }
  const call = currentCall.value;
  const rawCall = call ? toRaw(call) : null;
  const applicationId = rawCall?.customParameters?.get("applicationId");
  if (!applicationId) {
    return undefined;
  }
  return {
    name: ROUTE_DEAL_PROGRESS,
    params: { id: applicationId },
  };
});

const infoRows = computed(() => {
  const sections = [
    {
      name: "callee-name",
      iconOptions: {
        icon: IconProfileSmall,
        viewBox: "0 0 16 16",
      },
      value: contactName.value,
    },
    {
      name: "business-name",
      iconOptions: {
        icon: IconDeals,
        viewBox: "10 10 20 20",
      },
      value: businessName.value,
      link: dealLink.value,
    },
    {
      name: "partner-name",
      iconOptions: {
        icon: IconPersons,
        viewBox: "10 10 20 20",
      },
      value: partnerName.value,
    },
  ];
  return sections.filter((section) => !!section.value);
});

const getRouteHref = (route: RouteLocationRaw): string => {
  return router.resolve(route).href;
};

const handleKeypadPress = (key: string) => {
  activeCall.value?.sendDigits(key);
  trackEvent(AnalyticsEvent.TwilioDtmfSent);
};

const acceptCall = async () => {
  if (!incomingCall.value || !device.value) {
    return;
  }

  callStore.stopIncomingSound();

  try {
    const call = await toRaw(device.value).connect({
      connectToken: toRaw(incomingCall.value).connectToken,
    });

    // Clear incoming, set active
    callStore.setActiveCall(call);
    showCallTimer.value = true;

    // Register call listeners
    call.on(TwilioCallActions.Disconnect, handleCallEnded);
    call.on(TwilioCallActions.Cancel, handleCallEnded);
    call.on(TwilioCallActions.Mute, (isMuted: boolean) => {
      isMicrophoneMuted.value = isMuted;
    });

    // Show call notes modal for incoming calls
    showCallNotesModal(call.customParameters);
    trackEvent(AnalyticsEvent.TwilioCallAccepted);
  } catch (error) {
    showMessage(t("ERRORS.TWILIO.FAILED_TO_ACCEPT_CALL"), "error");
    trackEvent(AnalyticsEvent.TwilioError, {
      description: TwilioCallErrors.ConnectionError,
      explanation: t("ERRORS.TWILIO.FAILED_TO_ACCEPT_CALL"),
    });
    callStore.clearCallState();
  }
};

/**
 * Set up the call dialog to show the notes modal after accepting an incoming call
 */
const showCallNotesModal = (customParameters: Map<string, string>) => {
  const callSessionId = customParameters.get("callSessionId");
  if (!callSessionId) {
    return;
  }

  const callerIntegrationPartner =
    customParameters.get("callerIntegrationPartner") ?? "";
  const callerBusinessName = customParameters.get("callerBusinessName") ?? "";

  callDialog.value = {
    id: Number(callSessionId),
    type: CommunicationType.call,
    data: {
      id: Number(callSessionId),
      session_id: callSessionId,
      status: CallSessionStatus.InProgress,
      status_label: t("COMMON.IN_PROGRESS"),
      call_disposition: "",
      call_sessionable: {
        business: {
          business_legal_name: callerBusinessName,
        },
        client_name: callerIntegrationPartner,
      },
      is_outgoing: false,
    },
  };
};

const toggleKeypad = () => {
  isKeypadVisible.value = !isKeypadVisible.value;
  emit("keypad:toggle", isKeypadVisible.value);
  trackEvent(AnalyticsEvent.TwilioKeypadToggled, {
    action: isKeypadVisible.value ? "opened" : "closed",
  });
};

const rejectCall = () => {
  if (incomingCall.value) {
    toRaw(incomingCall.value).reject();
  }
  callStore.clearCallState();
  callStore.notifyCallEnded();
  trackEvent(AnalyticsEvent.TwilioCallRejected);
};

const disconnectCall = () => {
  const direction = callDirection.value;
  if (activeCall.value) {
    toRaw(activeCall.value).disconnect();
  }
  if (device.value && toRaw(device.value).isBusy) {
    toRaw(device.value).calls?.forEach((c) => c.disconnect());
  }
  callStore.clearCallState();
  callStore.notifyCallEnded();
  trackEvent(AnalyticsEvent.TwilioCallDisconnected, { direction });
};

const toggleMute = () => {
  if (activeCall.value) {
    const newMuteState = !isMicrophoneMuted.value;
    toRaw(activeCall.value).mute(newMuteState);
    trackEvent(AnalyticsEvent.TwilioMicrophoneToggled, {
      action: newMuteState ? "muted" : "unmuted",
    });
  }
};

const toggleSilence = () => {
  if (isRingtoneSilenced.value) {
    callStore.playIncomingSound();
    storeIsRingtoneSilenced.value = false;
    isRingtoneSilenced.value = false;
    trackEvent(AnalyticsEvent.TwilioRingtoneToggled, { action: "unmuted" });
  } else {
    callStore.stopIncomingSound();
    storeIsRingtoneSilenced.value = true;
    isRingtoneSilenced.value = true;
    callStore.notifyCallSilenced();
    trackEvent(AnalyticsEvent.TwilioRingtoneToggled, { action: "silenced" });
  }
};

const handleCallEnded = () => {
  callStore.clearCallState();
};

const resetState = () => {
  callStore.resetCallState();
};

const authorizeUseMedia = async () => {
  try {
    const permissions = await navigator.permissions.query({
      name: "microphone" as PermissionName,
    });
    if (permissions?.state !== "granted") {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    }
    await navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => (audioDevices.value = devices));
  } catch (err) {
    showMessage(t("TWILIO.DEVICE_AUTHORIZATION_ERROR"), "error");
    trackEvent(AnalyticsEvent.TwilioError, {
      description: TwilioCallErrors.DeviceAuthorizationError,
      explanation: t("TWILIO.DEVICE_AUTHORIZATION_ERROR"),
    });
    Sentry.captureException(err);
  }
};

const setDeviceSettings = (
  { deviceId, label }: { deviceId: string; label: string },
  type: "input" | "output"
) => {
  if (!device.value) {
    return;
  }
  if (type === "input") {
    previousDeviceIds.value[type] = selectedInputDeviceId.value;
    selectedInputDeviceId.value = deviceId;
    toRaw(device.value).audio?.setInputDevice(deviceId);
  } else {
    previousDeviceIds.value[type] = selectedOutputDeviceId.value;
    selectedOutputDeviceId.value = deviceId;
    toRaw(device.value).audio?.speakerDevices?.set(deviceId);
  }
  saveDevicePreference({ type, deviceId, label });
  trackEvent(AnalyticsEvent.TwilioAudioDeviceSelected, { type });
};

watch(
  [device, audioDevices],
  ([newDevice, newAudioDevices]) => {
    if (!newDevice || !newAudioDevices || hasRestoredDevices.value) {
      return;
    }
    hasRestoredDevices.value = true;

    const inputDeviceId = restoreDevice({
      type: "input",
      availableDevices: groupedDevices.value.input,
    });
    if (inputDeviceId) {
      selectedInputDeviceId.value = inputDeviceId;
      toRaw(newDevice).audio?.setInputDevice(inputDeviceId);
    }

    const outputDeviceId = restoreDevice({
      type: "output",
      availableDevices: groupedDevices.value.output,
    });
    if (outputDeviceId) {
      selectedOutputDeviceId.value = outputDeviceId;
      toRaw(newDevice).audio?.speakerDevices?.set(outputDeviceId);
    }
  },
  { immediate: true }
);

onMounted(async () => {
  await authorizeUseMedia();
});
// Note: Do NOT clear call state on unmount - the call should continue
// even if PiP is closed. Call state is managed by TwilioPiPManager.
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.call-session-info-row {
  @apply flex h-[23px] items-center justify-start space-x-2;

  .icon {
    @apply flex-none text-label;
  }
}

.dialer-round-button {
  @apply flex size-10 cursor-pointer items-center justify-center rounded-full;
}

.twilio-dialer-actions {
  @apply mt-auto flex w-full items-center justify-center space-x-3 p-4;
}

.twilio-pip-dialer {
  font-family:
    system-ui,
    -apple-system,
    sans-serif;
  min-height: 100%;
}
</style>
